
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { Gamepad, Zap, Award, Sparkles, TimerReset, Target } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ColorTunnel = () => {
  const intl = useIntl();

  return (
    <>
      <Helmet>
        <title>Color Tunnel - Thrilling 3D Arcade Running Game | Play Free Online</title>
        <meta name="description" content="Play Color Tunnel, an exciting 3D arcade game where you navigate through colorful tunnels avoiding obstacles. Test your reflexes and aim for the highest score!" />
        <meta name="keywords" content="Color Tunnel, arcade game, running game, online game, free game, 3D game, reflexes game" />
        <link rel="canonical" href="https://pokedle.cc/color-tunnel" />
      </Helmet>

      <div className="bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 min-h-screen text-white">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-black/40 z-10"></div>
          <div 
            className="h-[50vh] bg-[url('https://i.ibb.co/GpzZyFn/color-tunnel-hero.jpg')] bg-cover bg-center"
            style={{ backgroundPosition: "50% 30%" }}
          ></div>
          <div className="container mx-auto px-4 py-12 relative z-20 -mt-32">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-300 to-pink-300">
                Color Tunnel
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-indigo-200">
                Navigate through an endless, kaleidoscopic tunnel at high speed while dodging obstacles in this thrilling arcade game
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className="flex items-center bg-indigo-800 bg-opacity-50 backdrop-blur-sm p-3 rounded-lg">
                  <Gamepad className="h-6 w-6 mr-2 text-indigo-400" />
                  <span>Arcade Game</span>
                </div>
                <div className="flex items-center bg-indigo-800 bg-opacity-50 backdrop-blur-sm p-3 rounded-lg">
                  <Zap className="h-6 w-6 mr-2 text-pink-400" />
                  <span>Fast-Paced</span>
                </div>
                <div className="flex items-center bg-indigo-800 bg-opacity-50 backdrop-blur-sm p-3 rounded-lg">
                  <Award className="h-6 w-6 mr-2 text-yellow-400" />
                  <span>Skill-Based</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Game Section */}
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-5xl mx-auto bg-black/30 backdrop-blur-md p-4 rounded-xl border border-indigo-500/30 shadow-lg">
            <h2 className="text-2xl font-bold mb-4 text-center text-indigo-300">Play Color Tunnel</h2>
            <div className="aspect-video w-full overflow-hidden rounded-lg border-2 border-purple-500/50 shadow-[0_0_15px_rgba(139,92,246,0.3)]">
              <iframe 
                src="https://ubg365.github.io/color-tunnel/" 
                style={{ width: '100%', height: '100%' }} 
                frameBorder="0" 
                allow="gamepad *;" 
                title="Color Tunnel Game"
              ></iframe>
            </div>
            <div className="mt-4 text-center">
              <p className="text-indigo-200">
                <span className="mr-2">💡</span> 
                Use the left and right arrow keys or A/D keys to move and avoid obstacles
              </p>
            </div>
          </div>
        </div>

        {/* Game Description */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center text-transparent bg-clip-text bg-gradient-to-r from-pink-300 to-indigo-300">
              About Color Tunnel
            </h2>
            
            <div className="prose prose-lg prose-invert max-w-none mb-10">
              <p className="text-indigo-100 leading-relaxed mb-6">
                Color Tunnel is a thrilling running arcade game where your mission is to achieve the highest score while navigating through a tunnel filled with moving obstacles. The game offers an adrenaline-pumping experience that tests your reflexes and reaction time as you zoom through vibrant, colorful tunnels at increasing speeds.
              </p>
              
              <div className="flex flex-col md:flex-row gap-8 mb-8">
                <div className="md:w-1/2 bg-gradient-to-br from-indigo-900/60 to-purple-900/60 p-6 rounded-xl border border-indigo-500/30">
                  <h3 className="text-xl font-bold mb-3 flex items-center">
                    <Target className="h-5 w-5 mr-2 text-pink-400" />
                    Gameplay
                  </h3>
                  <p className="text-indigo-100 leading-relaxed">
                    The gameplay is straightforward yet challenging – your primary objective is to avoid all red obstacles within the tunnel. As you progress further into the game, your level increases, and with each level up, the tunnel transforms, offering new visual experiences and challenges. The control mechanism is simple, requiring only the right and left arrow keys or A/D keys to navigate. Your character moves automatically forward, and you need to shift left or right to avoid collisions with the various obstacles.
                  </p>
                </div>
                
                <div className="md:w-1/2 bg-gradient-to-br from-purple-900/60 to-pink-900/60 p-6 rounded-xl border border-purple-500/30">
                  <h3 className="text-xl font-bold mb-3 flex items-center">
                    <Sparkles className="h-5 w-5 mr-2 text-yellow-400" />
                    Visual Experience
                  </h3>
                  <p className="text-indigo-100 leading-relaxed">
                    One of the most captivating aspects of Color Tunnel is its vibrant visual design. The game features a kaleidoscope of colors that shift and change as you progress, creating an immersive and psychedelic experience. The tunnels are rendered in bright, neon colors that contrast beautifully with the obstacles, making for a visually stimulating journey. The fluid animation and dynamic color changes enhance the sense of speed and motion, pulling you deeper into the gameplay experience.
                  </p>
                </div>
              </div>
              
              <h3 className="text-2xl font-bold mb-4 text-pink-300">The Color Tunnel Challenge</h3>
              
              <p className="text-indigo-100 leading-relaxed mb-6">
                Color Tunnel is an extremely fast-paced game that demands skillful reactions and lightning-fast reflexes. You'll travel through the tunnel at high speed, with movement happening automatically. This automatic progression presents a significant challenge for many players, especially as your speed increases over time, requiring even quicker decision-making.
              </p>
              
              <p className="text-indigo-100 leading-relaxed mb-6">
                The tunnel is populated with various obstacles of different shapes, including triangles, hemispheres, and cylinders. These obstacles don't just appear in static positions – they move unpredictably, requiring you to constantly adapt your strategy. You'll need to maneuver through hemispheres while avoiding collisions with moving cylinders and triangles. Each type of obstacle requires a different approach to navigate safely.
              </p>
              
              <p className="text-indigo-100 leading-relaxed mb-6">
                Adding to the difficulty is the varying speed of these obstacles. Some remain stationary, others move slowly, and some zip across your path at high velocity. This changing pace continually presents new challenges, forcing you to quickly adapt to your surroundings. A single collision with any red obstacle results in game over, making precision and quick thinking essential skills for success.
              </p>
              
              <div className="bg-gradient-to-r from-indigo-800/50 to-purple-800/50 p-6 rounded-xl border border-indigo-500/30 mb-8">
                <h3 className="text-xl font-bold mb-3 flex items-center">
                  <Award className="h-5 w-5 mr-2 text-yellow-400" />
                  Three Best Ways to Overcome Obstacles
                </h3>
                <ol className="list-decimal pl-5 space-y-3 text-indigo-100">
                  <li>
                    <strong className="text-pink-300">Practice Makes Perfect:</strong> The best approach to mastering Color Tunnel is consistent practice. Regular gameplay helps you adapt to the game's speed and challenges. Initially, you may find it difficult to progress, with frequent restarts. However, once you acclimate to the pace, the game becomes increasingly enjoyable and rewarding.
                  </li>
                  <li>
                    <strong className="text-pink-300">Target the Intersections:</strong> When navigating through obstacles, aim for the intersection points between two shapes. These junctions typically offer the safest passage, providing enough space to maneuver without collision.
                  </li>
                  <li>
                    <strong className="text-pink-300">Platform Migration:</strong> The tunnel in Color Tunnel consists of multiple platforms that you can traverse. You can move from one platform to another, whether it's above or below your current position. This flexibility allows you to migrate to empty platforms to avoid obstacles. Be aware that these platforms move and rotate, requiring you to adjust your position accordingly to maintain safety.
                  </li>
                </ol>
              </div>
              
              <h3 className="text-2xl font-bold mb-4 text-pink-300">Key Game Parameters to Monitor</h3>
              
              <p className="text-indigo-100 leading-relaxed mb-6">
                When playing Color Tunnel, it's essential to keep track of three primary parameters: distance, level, and speed. These metrics provide valuable information about your progress and performance.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-b from-purple-900/60 to-indigo-900/60 p-5 rounded-xl border border-purple-500/30">
                  <h4 className="text-lg font-bold mb-2 text-pink-300 flex items-center">
                    <Target className="h-5 w-5 mr-2" />
                    Distance
                  </h4>
                  <p className="text-indigo-100 text-sm leading-relaxed">
                    Located at the top-left of the screen, this parameter displays how far you've traveled. Distance is perhaps the most significant indicator as it directly reflects your achievement in the game. Your primary goal is to cover as much distance as possible, with higher distances equating to higher scores and showcasing your control mastery.
                  </p>
                </div>
                
                <div className="bg-gradient-to-b from-purple-900/60 to-indigo-900/60 p-5 rounded-xl border border-purple-500/30">
                  <h4 className="text-lg font-bold mb-2 text-pink-300 flex items-center">
                    <Award className="h-5 w-5 mr-2" />
                    Level
                  </h4>
                  <p className="text-indigo-100 text-sm leading-relaxed">
                    Displayed at the top-center of the screen, the level indicator shows how many levels you've completed. While the track is endless, it's divided into segments representing individual levels. Higher levels correspond to greater distances traveled. This metric offers a simpler way to track progress compared to the potentially large distance numbers.
                  </p>
                </div>
                
                <div className="bg-gradient-to-b from-purple-900/60 to-indigo-900/60 p-5 rounded-xl border border-purple-500/30">
                  <h4 className="text-lg font-bold mb-2 text-pink-300 flex items-center">
                    <Zap className="h-5 w-5 mr-2" />
                    Speed
                  </h4>
                  <p className="text-indigo-100 text-sm leading-relaxed">
                    This parameter indicates your current traveling velocity. Speed varies depending on the section of the tunnel you're in. Higher speeds introduce greater difficulty, requiring increased focus and quicker reactions to avoid obstacles. Monitoring your speed helps you anticipate the level of challenge ahead.
                  </p>
                </div>
              </div>
              
              <p className="text-indigo-100 leading-relaxed mb-8">
                If you're too engrossed in gameplay to monitor these metrics during a run, don't worry. After each round, Color Tunnel provides a summary of your performance, including your distance traveled and notable achievements. These post-game statistics offer an opportunity to assess your performance and set goals for improvement in subsequent attempts.
              </p>
              
              <h3 className="text-2xl font-bold mb-4 text-pink-300">Tunnel Types and Obstacles</h3>
              
              <p className="text-indigo-100 leading-relaxed mb-6">
                Color Tunnel features two distinctive tunnel types, each with its own unique characteristics and challenges:
              </p>
              
              <div className="flex flex-col md:flex-row gap-8 mb-8">
                <div className="md:w-1/2 bg-gradient-to-br from-indigo-900/60 to-purple-900/60 p-6 rounded-xl border border-indigo-500/30">
                  <h4 className="text-lg font-bold mb-3 text-pink-300">Interior Tunnel</h4>
                  <p className="text-indigo-100 leading-relaxed">
                    The first tunnel type requires you to navigate from within its interior. You'll maneuver inside the tunnel, avoiding obstacles that appear along its inner surface. This perspective creates a unique spatial awareness challenge as you must judge distances and timings from within the enclosed space.
                  </p>
                </div>
                
                <div className="md:w-1/2 bg-gradient-to-br from-purple-900/60 to-pink-900/60 p-6 rounded-xl border border-purple-500/30">
                  <h4 className="text-lg font-bold mb-3 text-pink-300">Exterior Tunnel</h4>
                  <p className="text-indigo-100 leading-relaxed">
                    The second tunnel variant has you moving along the outside surface of the tunnel. This external perspective offers a different visual experience and requires you to adapt your movement strategies. Navigating the exterior of the tunnel presents its own set of challenges and opportunities for skillful maneuvering.
                  </p>
                </div>
              </div>
              
              <p className="text-indigo-100 leading-relaxed mb-6">
                Throughout both tunnel types, you'll encounter four main shapes of obstacles:
              </p>
              
              <ul className="list-disc pl-6 mb-8 space-y-2 text-indigo-100">
                <li><strong className="text-pink-300">Triangular blocks:</strong> These angular obstacles require precise timing to avoid their sharp edges.</li>
                <li><strong className="text-pink-300">Rectangular blocks:</strong> These present broader obstacles that may require more significant movement to navigate around.</li>
                <li><strong className="text-pink-300">Square blocks:</strong> Similar to rectangular blocks but with equal dimensions, requiring strategic positioning to avoid.</li>
                <li><strong className="text-pink-300">Curved blocks:</strong> These hemispheric or cylindrical obstacles may roll or rotate, adding an additional layer of complexity to navigation.</li>
              </ul>
              
              <p className="text-indigo-100 leading-relaxed mb-8">
                As a running game enthusiast, Color Tunnel offers a unique experience that stands out from other titles in the genre. The game's colorful, psychedelic tunnels provide an eye-catching visual experience that immediately captures attention from the first play. What's more, the tunnel colors change with each playthrough, ensuring a fresh visual experience every time you play.
              </p>
              
              <p className="text-indigo-100 leading-relaxed">
                Running games have garnered millions of players worldwide, and their popularity continues to grow in the online gaming landscape. Color Tunnel builds on the running game theme but presents it in a first-person perspective, creating a more immersive experience. The game's unique blend of simple controls, challenging gameplay, and striking visuals sets it apart from other running games. To truly appreciate what makes Color Tunnel special, there's no substitute for experiencing it firsthand—start playing and discover the exhilarating rush of navigating the colorful tunnels at breakneck speeds!
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="container mx-auto px-4 py-12 bg-gradient-to-b from-transparent to-indigo-950/50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center text-transparent bg-clip-text bg-gradient-to-r from-pink-300 to-indigo-300">
              Frequently Asked Questions
            </h2>
            
            <Accordion type="single" collapsible className="bg-black/20 backdrop-blur-sm rounded-xl border border-indigo-500/30 overflow-hidden">
              <AccordionItem value="item-1" className="border-b border-indigo-500/30">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-medium hover:text-pink-300 transition-colors">
                  How do I play Color Tunnel?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 text-indigo-200">
                  To play Color Tunnel, use the left and right arrow keys or the A and D keys to move your character. Your goal is to avoid hitting the red obstacles while traveling through the colorful tunnel. The game automatically moves you forward, so you only need to focus on dodging obstacles by moving left or right.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2" className="border-b border-indigo-500/30">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-medium hover:text-pink-300 transition-colors">
                  Is there an end to the Color Tunnel game?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 text-indigo-200">
                  No, Color Tunnel is an endless runner game. The objective is not to reach an endpoint but to travel as far as possible while avoiding obstacles. The game becomes progressively more challenging as your speed increases and the obstacle patterns become more complex.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3" className="border-b border-indigo-500/30">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-medium hover:text-pink-300 transition-colors">
                  How can I improve my score in Color Tunnel?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 text-indigo-200">
                  To improve your score in Color Tunnel, focus on these strategies: 1) Practice regularly to improve your reflexes, 2) Look ahead to anticipate obstacles rather than focusing only on what's directly in front of you, 3) Aim for the gaps between obstacles, 4) Stay calm when the speed increases, and 5) Learn the patterns of obstacle movements to better predict safe paths.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4" className="border-b border-indigo-500/30">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-medium hover:text-pink-300 transition-colors">
                  Why does the tunnel color change in the game?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 text-indigo-200">
                  The tunnel colors change as you progress through different levels in the game. This color-shifting aspect not only creates a visually stimulating experience but also indicates your advancement. Each new color scheme represents a level up, adding to the psychedelic and dynamic nature of the gameplay experience.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-5" className="border-b border-indigo-500/30">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-medium hover:text-pink-300 transition-colors">
                  Can I play Color Tunnel on mobile devices?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 text-indigo-200">
                  Yes, Color Tunnel is compatible with mobile devices. On touchscreen devices, you can control your movement by tapping on the left or right side of the screen to move in that direction. The game's simple control scheme makes it well-suited for both desktop and mobile play.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-6" className="border-b border-indigo-500/30">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-medium hover:text-pink-300 transition-colors">
                  Are there different difficulty levels in Color Tunnel?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 text-indigo-200">
                  While Color Tunnel doesn't have explicit difficulty settings, the game's difficulty increases naturally as you progress. The further you travel, the faster you move and the more complex the obstacle patterns become. This progressive difficulty scaling ensures that the game remains challenging regardless of your skill level.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-7" className="border-b-0">
                <AccordionTrigger className="px-6 py-4 text-left text-lg font-medium hover:text-pink-300 transition-colors">
                  What makes Color Tunnel different from other running games?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 text-indigo-200">
                  Color Tunnel stands out from other running games due to its unique first-person perspective within a 3D tunnel, vibrant color schemes that change as you progress, and the distinctive obstacle types that require different avoidance strategies. The game combines simple controls with challenging gameplay and psychedelic visuals to create an experience that's both accessible and engaging for players of all skill levels.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
        
        {/* Game Tips Section */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center text-transparent bg-clip-text bg-gradient-to-r from-pink-300 to-indigo-300">
              Pro Tips for Color Tunnel Masters
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gradient-to-br from-indigo-900/60 to-purple-900/60 p-6 rounded-xl border border-indigo-500/30">
                <h3 className="text-xl font-bold mb-3 flex items-center">
                  <TimerReset className="h-5 w-5 mr-2 text-pink-400" />
                  Timing is Everything
                </h3>
                <p className="text-indigo-100 leading-relaxed">
                  In Color Tunnel, mastering the timing of your movements is crucial. Rather than making constant adjustments, try to make deliberate, well-timed moves. Observe the pattern of obstacles and plan your path accordingly. Sometimes, it's better to wait momentarily before making a move, especially when multiple obstacles are approaching in quick succession.
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-900/60 to-pink-900/60 p-6 rounded-xl border border-purple-500/30">
                <h3 className="text-xl font-bold mb-3 flex items-center">
                  <Zap className="h-5 w-5 mr-2 text-yellow-400" />
                  Stay Centered When Possible
                </h3>
                <p className="text-indigo-100 leading-relaxed">
                  Unless obstacles force you to the sides, try to stay centered in the tunnel whenever possible. This central position gives you the most flexibility to move in either direction when obstacles appear. Being centered reduces the distance you need to travel to avoid obstacles and provides better visibility of approaching challenges.
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-pink-900/60 to-indigo-900/60 p-6 rounded-xl border border-pink-500/30">
                <h3 className="text-xl font-bold mb-3 flex items-center">
                  <Sparkles className="h-5 w-5 mr-2 text-indigo-400" />
                  Focus on the Horizon
                </h3>
                <p className="text-indigo-100 leading-relaxed">
                  While it's tempting to focus on obstacles directly in front of you, successful players train themselves to look further ahead down the tunnel. By focusing on the horizon, you can anticipate obstacles earlier and plan your movements more effectively. This forward-looking approach is especially important as your speed increases in later levels.
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-indigo-900/60 to-pink-900/60 p-6 rounded-xl border border-indigo-500/30">
                <h3 className="text-xl font-bold mb-3 flex items-center">
                  <Award className="h-5 w-5 mr-2 text-pink-400" />
                  The Rhythm Method
                </h3>
                <p className="text-indigo-100 leading-relaxed">
                  Color Tunnel has a certain rhythm to it, especially in how obstacles are positioned and move. Try to get into the flow of the game and develop a sense of this rhythm. Many top players report entering a flow state where their movements become almost instinctual rather than calculated. Regular practice helps develop this rhythmic sense of the game.
                </p>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-xl text-indigo-300 italic">
                "The true mark of a Color Tunnel master isn't just distance traveled, but the grace with which they navigate the chaos of the tunnel."
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ColorTunnel;
